import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Phone, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "../lib/enquiry";
import { IMAGES, CONTACT } from "../data/content";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  check_in: "",
  check_out: "",
  guests: "2 Adults",
  message: "",
};

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("We couldn't send your enquiry. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="lead-form-section"
      className="bg-[#243E2C] text-[#EFECE4]"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12">
        {/* Visual / contact column */}
        <div className="lg:col-span-5 relative min-h-[320px] lg:min-h-full">
          <img
            src={IMAGES.dining}
            alt="Fireside dining at Nine Furlongs"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E20] via-[#1A2E20]/50 to-transparent" />
          <div className="relative p-10 md:p-14 flex flex-col justify-end h-full">
            <p className="uppercase text-xs tracking-[0.25em] text-[#C4A575] font-semibold">
              Reservations
            </p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              Begin your journey on Nine Furlongs
            </h2>
            <div className="mt-8 space-y-4 text-sm">
              <a href={CONTACT.phoneHref} className="flex items-center gap-3 hover:text-[#C4A575] transition-colors" data-testid="form-call-link">
                <Phone size={16} className="text-[#C4A575]" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-[#C4A575] transition-colors">
                <Mail size={16} className="text-[#C4A575]" /> {CONTACT.email}
              </a>
              <p className="flex items-start gap-3 text-[#EFECE4]/80">
                <MapPin size={16} className="text-[#C4A575] mt-0.5 shrink-0" />
                {CONTACT.address}
              </p>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="lg:col-span-7 p-8 md:p-14 lg:p-20">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center py-16"
                data-testid="lead-form-success"
              >
                <CheckCircle2 size={56} className="text-[#C4A575]" strokeWidth={1.3} />
                <h3 className="font-serif text-3xl md:text-4xl mt-6">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="text-[#EFECE4]/75 mt-4 max-w-md leading-relaxed">
                  Your enquiry has reached our reservations team. We'll craft a
                  personalised itinerary and reach out within a few hours. For an
                  instant response, feel free to call us.
                </p>
                <a
                  href={CONTACT.phoneHref}
                  className="mt-8 inline-flex items-center gap-2 bg-[#C4A575] text-primary px-8 py-4 uppercase text-sm tracking-[0.18em] hover:bg-[#b3935f] transition-colors"
                  data-testid="success-call-button"
                >
                  <Phone size={16} /> Call {CONTACT.phone}
                </a>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-7"
                data-testid="lead-form"
              >
                <div>
                  <p className="uppercase text-xs tracking-[0.25em] text-[#C4A575] font-semibold">
                    Enquire Now
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl mt-3">
                    Check availability & rates
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Full Name *">
                    <input
                      data-testid="form-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={inputCls}
                      required
                    />
                  </Field>
                  <Field label="Phone *">
                    <input
                      data-testid="form-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91"
                      className={inputCls}
                      required
                    />
                  </Field>
                </div>

                <Field label="Email *">
                  <input
                    data-testid="form-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                    required
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <Field label="Check In">
                    <input
                      data-testid="form-checkin-input"
                      type="date"
                      value={form.check_in}
                      onChange={update("check_in")}
                      className={`${inputCls} [color-scheme:dark]`}
                    />
                  </Field>
                  <Field label="Check Out">
                    <input
                      data-testid="form-checkout-input"
                      type="date"
                      value={form.check_out}
                      onChange={update("check_out")}
                      className={`${inputCls} [color-scheme:dark]`}
                    />
                  </Field>
                  <Field label="Guests">
                    <select
                      data-testid="form-guests-input"
                      value={form.guests}
                      onChange={update("guests")}
                      className={`${inputCls} cursor-pointer`}
                    >
                      {["1 Adult", "2 Adults", "2 Adults + 1 Child", "Family (4+)", "Group"].map(
                        (g) => (
                          <option key={g} className="bg-[#243E2C]">
                            {g}
                          </option>
                        )
                      )}
                    </select>
                  </Field>
                </div>

                <Field label="Message (optional)">
                  <textarea
                    data-testid="form-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={2}
                    placeholder="Tell us about your dream stay..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-button"
                  className="w-full bg-[#C4A575] text-primary py-4 uppercase text-sm tracking-[0.2em] font-medium hover:bg-[#b3935f] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send Enquiry"
                  )}
                </button>
                <p className="text-xs text-[#EFECE4]/50 text-center">
                  We respect your privacy. No spam, ever.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full bg-transparent border-b border-[#EFECE4]/30 focus:border-[#C4A575] py-2.5 text-[#EFECE4] placeholder-[#EFECE4]/40 focus:outline-none transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.15em] text-[#EFECE4]/60 mb-1.5 block">
      {label}
    </span>
    {children}
  </label>
);
